import { defineComponent, reactive, ref, SetupContext, computed } from '@vue/composition-api';
import MessageConst from '@/shared/message-const';
import SystemConst from '@/shared/system-const';
import CommonFunction from '@/shared/common-function';
import vueRouter from '@/router/index';                                           // ルート情報取得
import { UserType } from '@/shared/type';                                         // 型定義をimport
import CommonMessageDialog from '@/components/common/common-message-dialog.component.vue';
import HttpStatus from 'http-status-codes';                                       // HTTPステータスコード取得用

/**
 * 文字列定数の型
 */
interface StringConst {
    [key: string]: string;
}

// reactiveDataの型定義
export type reactiveData = {
    windowSize: { x: number; y: number };                       // Windowサイズ
    user: UserType;                                             // ユーザー情報
    displayUserId: string;
};

export default defineComponent({
    components: { CommonMessageDialog },
    setup(props, ctx: SetupContext) {
        const formHeight = SystemConst.FORM_HEIGHT.HEIGHT; // フォーム高さ調整用変数
        const router = vueRouter;                               // ルータ
        const commonRef = ref<any>(null);                       // ダイアログ
        const initialPulldown = { text: '', value: '' };
        const state = <reactiveData>reactive({
            windowSize: { x: 0, y: 0 },                         // ウィンドウサイズ
            user: {
                identificationCode: '',                         // 識別コード
                organizationId: initialPulldown,                // 所属組織（text: 組織名称, value: 組織ID）（登録/更新処理で利用）
                organizationName: '',                           // 所属組織名
                accountDivision: '',                            // アカウント区分
                isOrganizationManager: false,                   // 組織管理者権限
                lastName: '',                                   // 姓（登録/更新処理で利用）
                firstName: '',                                  // 名（登録/更新処理で利用）
                fullName: '',                                   // 氏名
                lastNameKana: '',                               // 姓（かな）（登録/更新処理で利用）
                firstNameKana: '',                              // 名（かな）（登録/更新処理で利用）
                fullNameKana: '',                               // 氏名（かな）
                autoSetMail: '',                                // メールアドレス自動生成（登録/更新処理で利用）
                mail: '',                                       // アドレス部（登録/更新処理で利用）
                domain: '',                                     // ドメイン（登録/更新処理で利用）
                mailAddress: '',                                // メールアドレス
                autoSetPassword: '',                            // パスワード自動生成（登録/更新処理で利用）
                password: '',                                   // パスワード（登録/更新処理で利用）
                rePassword: '',                                 // パスワード再入力（登録/更新処理で利用）
                postName: '',                                   // 職
                fiscalYear: initialPulldown,                    // 作成年度（text: 和暦, value: 西暦）（登録/更新処理で利用）
                fiscalYearText: '',                             // 作成年度
                isEnabled: true,                                // 状態（登録/更新処理で利用）
                enabled: ''                                     // 状態
            },
            displayUserId: ''                                   // 画面表示用のユーザーID（開発ツール等で変更できてしまうためback側に渡す変数と分ける）
        });
        let userId = '';                                        // 削除対象のユーザーID
        const accountDivisionName: StringConst = {
            // アカウント区分の選択された値
            a1: SystemConst.ACCOUNT_DIVISION_NAME.TEACHER,
            b1: SystemConst.ACCOUNT_DIVISION_NAME.STUDENT,
            b2: SystemConst.ACCOUNT_DIVISION_NAME.GENERAL,
            c1: SystemConst.ACCOUNT_DIVISION_NAME.REPRESENTATIVE,
            s1: SystemConst.ACCOUNT_DIVISION_NAME.ADMIN
        };

        /**
         * ユーザー削除画面初期化処理
         */
        created();
        async function created() {
            // ユーザーID取得
            const getUserId = computed(() => ctx.root.$route.params.id);
            if (typeof getUserId.value !== 'undefined') {
                // ユーザーIDが格納されている場合
                // ユーザーIDを保存
                userId = getUserId.value;
                state.displayUserId = getUserId.value;
            }
            // titleIcon変更
            ctx.root.$store.commit('setTitleIcon', 'mdi-account-remove');

            // mainTitle変更
            ctx.root.$store.commit('setMainTitle', 'ユーザー削除');

            // 削除対象データを取得するAPIを呼び出す
            // パラメータ：ユーザーID
            const result = await ctx.root.$store.dispatch('user/getOperateUser',
                {
                    'userId': userId
                }
            );

            // 通信結果判別
            if (result.status !== HttpStatus.OK) {
                // HTTPステータスコードが200以外の場合
                if (result.status === HttpStatus.NOT_FOUND) {
                    // HTTPステータスコードが404の場合
                    // displayErrorMessageTransitionDialogメソッドでユーザー一覧画面に戻す
                    commonRef.value.displayErrorMessageTransitionDialog(result, '/users/list');
                } else {
                    // HTTPステータスコードが404以外の場合
                    // errorDisplayTransitionControlメソッドでエラー画面を表示する
                    commonRef.value.errorDisplayTransitionControl(result);
                }
                return;
            }

            // HTTPステータスコードが200の場合
            //   通信結果をstateの中に設定する
            state.user.identificationCode = result.data.operateUser['identificationCode'];
            state.user.isOrganizationManager = result.data.operateUser['isOrganizationManager'];
            state.user.organizationName = result.data.operateUser['organizationName'];
            state.user.mailAddress = result.data.operateUser['mail'];
            state.user.postName = result.data.operateUser['postName'];
            state.user.fiscalYearText = result.data.operateUser['fiscalYearText'];
            //   アカウント区分は結果をキーとした定数から取得した値を格納する
            const accountName = result.data.operateUser['accountDivision'];
            state.user.accountDivision = accountDivisionName[accountName];
            //   氏名は半角スペースを空けて結合する
            state.user.fullName = result.data.operateUser['lastName'] + ' ' + result.data.operateUser['firstName'];
            state.user.fullNameKana = result.data.operateUser['lastNameKana'] + ' ' + result.data.operateUser['firstNameKana'];
            //   状態は結果がtrueの時「有効」falseの時「無効」を格納する
            if (result.data.operateUser['isEnabled']) {
                state.user.enabled = SystemConst.ENABLED_NAME['VALID'];
            } else {
                state.user.enabled = SystemConst.ENABLED_NAME['INVALID'];
            }
        }

        /**
         * 削除ボタン押下時に確認メッセージダイアログ表示
         */
        function displayDialog() {
            const displayMessage = CommonFunction.replaceArray(MessageConst.S_CONFIRMATION, ['削除']);
            // 確認メッセージダイアログ表示
            commonRef.value.displayConfirmationMessageDialog(displayMessage);
        }

        /**
         * 削除画面にて、確認メッセージダイアログの「はい」を押下時の処理
         */
        async function processingData() {
            // ユーザー削除APIを呼び出す
            // パラメータ: ユーザーID
            const response = await ctx.root.$store.dispatch('user/deleteUser',
                {
                    'userId': userId
                }
            );

            // 通信結果判別
            if (response.status !== HttpStatus.OK) {
                // HTTPステータスコードが200以外の場合
                if (response.status === HttpStatus.NOT_FOUND) {
                    // HTTPステータスコードが404の場合
                    // displayErrorMessageTransitionDialogメソッドでユーザー一覧画面に戻す
                    commonRef.value.displayErrorMessageTransitionDialog(response, '/users/list');
                } else {
                    // HTTPステータスコードが404以外の場合
                    // errorDisplayTransitionControlメソッドでエラー画面を表示する
                    commonRef.value.errorDisplayTransitionControl(response);
                }
                return;
            }

            // HTTPステータスコードが200の場合
            //   削除完了メッセージダイアログを表示して一覧に遷移
            commonRef.value.displayMessageTransitionDialog(response, '/users/list');
        }

        /**
         * 削除画面にて、キャンセルボタン押下時の処理
         */
        function back() {
            router.back();
        }

        /**
         * ウィンドウサイズ変更監視
         * 変更されたウィンドウサイズを変数に保持する
         */
        function onResize(): void {
            state.windowSize = { x: window.innerWidth, y: window.innerHeight };
        }

        return {
            state,
            router,
            formHeight,
            commonRef,
            displayDialog,
            processingData,
            onResize,
            back
        };
    }
});
